import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Plus, Smile, SmilePlus, X } from 'lucide-react';
import type { MessageReactionSummary } from '../types';

const EmojiPickerPanel = lazy(() => import('./EmojiPickerPanel'));

// The quick choices; the full picker allows any emoji.
const REACTION_EMOJIS = ['👍', '❤️', '🙏', '😂', '🎉', '😮'];

function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);
  return ref;
}

function Popover({
  open, align, children,
}: { open: boolean; align: 'left' | 'right'; children: ReactNode }) {
  if (!open) return null;
  return (
    <div
      role="menu"
      className={`absolute bottom-full z-20 mb-2 rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg ${align === 'right' ? 'right-0' : 'left-0'}`}
    >
      {children}
    </div>
  );
}

function PickerFallback({ height }: { height: number }) {
  return (
    <div className="flex w-[320px] max-w-full items-center justify-center" style={{ height }}>
      <Loader2 size={20} className="animate-spin text-gray-300" />
    </div>
  );
}

/** Composer button that inserts an emoji into the message. */
export function EmojiPickerButton({
  onPick, disabled,
}: { onPick: (emoji: string) => void; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        disabled={disabled}
        aria-label="Add emoji"
        aria-expanded={open}
        className="h-full px-2.5 rounded-xl text-gray-500 hover:bg-gray-100 hover:text-navy transition disabled:opacity-50"
      >
        <Smile size={20} />
      </button>
      {open && (
        <div className="absolute bottom-full left-0 z-20 mb-2 max-w-[calc(100vw-1.5rem)] shadow-lg rounded-xl">
          <Suspense fallback={<PickerFallback height={380} />}>
            <EmojiPickerPanel onPick={onPick} />
          </Suspense>
        </div>
      )}
    </div>
  );
}

// Centered rather than anchored so the full picker is never clipped by the
// message list's scroll area.
function FullPickerDialog({ onPick, onClose }: { onPick: (emoji: string) => void; onClose: () => void }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-3" onClick={onClose}>
      <div className="relative" onClick={(event) => event.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute -top-3 -right-3 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-navy text-white shadow"
        >
          <X size={14} />
        </button>
        <Suspense fallback={<div className="rounded-xl bg-white"><PickerFallback height={420} /></div>}>
          <EmojiPickerPanel onPick={onPick} height={420} />
        </Suspense>
      </div>
    </div>,
    document.body,
  );
}

function displayName(user: { id: number; name: string }, currentUserId?: number) {
  return user.id === currentUserId ? 'You' : user.name;
}

function firstName(user: { id: number; name: string }, currentUserId?: number) {
  return user.id === currentUserId ? 'You' : user.name.split(' ')[0];
}

function WhoReacted({
  reactions, currentUserId, align,
}: { reactions: MessageReactionSummary[]; currentUserId?: number; align: 'left' | 'right' }) {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  const people = new Map<number, { id: number; name: string }>();
  for (const reaction of reactions) {
    for (const person of reaction.users ?? []) people.set(person.id, person);
  }
  const everyone = [...people.values()].sort(
    (a, b) => Number(b.id === currentUserId) - Number(a.id === currentUserId),
  );
  if (everyone.length === 0) return null;
  const shown = everyone.slice(0, 2).map((person) => firstName(person, currentUserId));
  const others = everyone.length - shown.length;
  const summary = others > 0
    ? `${shown.join(', ')} and ${others} other${others === 1 ? '' : 's'}`
    : shown.join(' and ');

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="text-xs text-gray-500 hover:text-navy hover:underline pointer-coarse:py-1"
      >
        {summary} reacted
      </button>
      <Popover open={open} align={align}>
        <ul className="w-64 max-h-64 overflow-y-auto px-1.5 py-1 text-sm">
          {reactions.map((reaction) => (
            <li key={reaction.emoji} className="flex gap-2 py-1">
              <span className="text-lg leading-6">{reaction.emoji}</span>
              <span className="text-gray-700 leading-6">
                {(reaction.users ?? []).map((person) => displayName(person, currentUserId)).join(', ')}
              </span>
            </li>
          ))}
        </ul>
      </Popover>
    </div>
  );
}

/** Reaction counts under a message; tapping one toggles your own. */
export function ReactionChips({
  reactions, onToggle, align, currentUserId, showWho = false,
}: {
  reactions: MessageReactionSummary[];
  onToggle: (emoji: string) => void;
  align: 'left' | 'right';
  currentUserId?: number;
  showWho?: boolean;
}) {
  if (reactions.length === 0) return null;
  return (
    <div className={`mt-1 flex flex-col gap-0.5 ${align === 'right' ? 'items-end' : 'items-start'}`}>
      <div className={`flex flex-wrap items-center gap-1 ${align === 'right' ? 'justify-end' : 'justify-start'}`}>
        {reactions.map((reaction) => {
          const who = (reaction.users ?? []).map((person) => displayName(person, currentUserId)).join(', ')
            || reaction.names.join(', ');
          return (
            <button
              key={reaction.emoji}
              type="button"
              onClick={() => onToggle(reaction.emoji)}
              title={who}
              aria-label={`${reaction.emoji} ${reaction.count}, ${who}`}
              aria-pressed={reaction.reacted}
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-sm transition ${
                reaction.reacted
                  ? 'border-navy/40 bg-navy/10 text-navy'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
              }`}
            >
              <span>{reaction.emoji}</span>
              <span className="text-xs font-medium">{reaction.count}</span>
            </button>
          );
        })}
      </div>
      {showWho && <WhoReacted reactions={reactions} currentUserId={currentUserId} align={align} />}
    </div>
  );
}

/** Opens the reaction choices for a message. */
export function AddReactionButton({
  onPick, align,
}: { onPick: (emoji: string) => void; align: 'left' | 'right' }) {
  const [open, setOpen] = useState(false);
  const [fullPicker, setFullPicker] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label="React"
        aria-expanded={open}
        className="flex items-center rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-navy pointer-coarse:px-1.5 pointer-coarse:py-2.5"
      >
        <SmilePlus size={15} />
      </button>
      <Popover open={open} align={align}>
        <div className="flex gap-0.5">
          {REACTION_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => {
                onPick(emoji);
                setOpen(false);
              }}
              aria-label={`React ${emoji}`}
              className="h-9 w-9 rounded-lg text-xl leading-none hover:bg-gray-100"
            >
              {emoji}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setFullPicker(true);
            }}
            aria-label="More emojis"
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-navy"
          >
            <Plus size={18} />
          </button>
        </div>
      </Popover>
      {fullPicker && (
        <FullPickerDialog
          onClose={() => setFullPicker(false)}
          onPick={(emoji) => {
            onPick(emoji);
            setFullPicker(false);
          }}
        />
      )}
    </div>
  );
}
